import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Shield, DollarSign, Lock, Eye, AlertTriangle, Settings,
  Save, RotateCcw, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  referral_window_days: 60,
  lock_after_first_conversion: true,
  enable_lifetime_commissions: true,
  pay_on_renewals: true,
  pay_on_upgrades: true,
  pay_on_downgrades: false,
  payout_threshold: 200,
  hold_period_days: 14,
  enable_clawbacks: true,
  require_terms_acceptance: true,
  require_stripe_connect: true,
  require_tax_info: true,
  block_self_referrals: true,
  enable_fraud_flags: true,
  allow_manual_attribution_override: true,
  allow_commission_override: true,
};

export default function AdminAffiliateRules() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  useEffect(() => {
    fetch(`${API}/affiliate/admin/settings`, { headers: headers() })
      .then(r => r.json()).then(d => { setSettings(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [headers]);

  const updateField = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/affiliate/admin/settings`, {
        method: 'PATCH', headers: headers(), body: JSON.stringify(settings),
      });
      if (res.ok) {
        const d = await res.json();
        setSettings(d);
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch { toast.error('Error saving settings'); }
    setSaving(false);
  };

  const restoreDefaults = () => {
    setSettings(prev => ({ ...prev, ...DEFAULTS }));
    toast.info('Defaults restored — click Save to apply');
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        {[...Array(4)].map((_, i) => <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}
      </div>
    );
  }

  if (!settings) return <div className="p-8 text-gray-500">Unable to load affiliate settings.</div>;

  const ToggleRow = ({ label, desc, field }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-[#0f1f33]">{label}</p>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <Switch checked={!!settings[field]} onCheckedChange={v => updateField(field, v)} data-testid={`toggle-${field}`} />
    </div>
  );

  const NumberInput = ({ label, field, suffix = "" }) => (
    <div className="flex items-center justify-between py-3">
      <p className="text-sm font-medium text-[#0f1f33]">{label}</p>
      <div className="flex items-center gap-2">
        <Input type="number" value={settings[field]} onChange={e => updateField(field, parseFloat(e.target.value) || 0)}
          className="w-24 text-right" data-testid={`input-${field}`} />
        {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  );

  const sections = [
    {
      icon: Lock, title: "Attribution", color: "text-blue-600", bg: "bg-blue-50",
      items: [
        { type: 'toggle', label: "Lock after first conversion", desc: "Attribution becomes permanent after the referred member's first paid conversion", field: "lock_after_first_conversion" },
        { type: 'info', label: "Attribution model", value: "Last-click (before lock)" },
        { type: 'number', label: "Referral window", field: "referral_window_days", suffix: "days" },
      ]
    },
    {
      icon: DollarSign, title: "Commissions", color: "text-emerald-600", bg: "bg-emerald-50",
      items: [
        { type: 'toggle', label: "Enable lifetime commissions", desc: "Partners earn on all future qualifying payments from referred members", field: "enable_lifetime_commissions" },
        { type: 'toggle', label: "Pay on renewals", desc: "Generate commission events when referred members renew", field: "pay_on_renewals" },
        { type: 'toggle', label: "Pay on upgrades", desc: "Generate commission events when referred members upgrade tier", field: "pay_on_upgrades" },
        { type: 'toggle', label: "Pay on downgrades", desc: "Generate commission events when referred members downgrade", field: "pay_on_downgrades" },
        { type: 'number', label: "Hold period", field: "hold_period_days", suffix: "days" },
      ]
    },
    {
      icon: Shield, title: "Financial Controls", color: "text-violet-600", bg: "bg-violet-50",
      items: [
        { type: 'toggle', label: "Enable clawbacks", desc: "Reverse commissions on refunds and chargebacks", field: "enable_clawbacks" },
        { type: 'number', label: "Payout threshold", field: "payout_threshold", suffix: "$" },
      ]
    },
    {
      icon: CheckCircle, title: "Requirements", color: "text-indigo-600", bg: "bg-indigo-50",
      items: [
        { type: 'toggle', label: "Require terms acceptance", desc: "Partners must accept terms before activation", field: "require_terms_acceptance" },
        { type: 'toggle', label: "Require Bank Account", desc: "Partners must add bank details before payout eligibility", field: "require_stripe_connect" },
        { type: 'toggle', label: "Require tax info", desc: "Partners must submit tax information before payouts", field: "require_tax_info" },
      ]
    },
    {
      icon: AlertTriangle, title: "Fraud Protection", color: "text-red-600", bg: "bg-red-50",
      items: [
        { type: 'toggle', label: "Block self-referrals", desc: "Prevent partners from referring their own accounts", field: "block_self_referrals" },
        { type: 'toggle', label: "Enable fraud flags", desc: "Automatically flag suspicious patterns for review", field: "enable_fraud_flags" },
      ]
    },
    {
      icon: Settings, title: "Admin Controls", color: "text-gray-600", bg: "bg-gray-50",
      items: [
        { type: 'toggle', label: "Allow attribution reassignment", desc: "Admin can manually change a member's attributed partner", field: "allow_manual_attribution_override" },
        { type: 'toggle', label: "Allow commission override", desc: "Admin can set custom commission rates per partner", field: "allow_commission_override" },
      ]
    },
  ];

  return (
    <div data-testid="affiliate-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-bold text-[#0f1f33]">Affiliate Rules Engine</h2>
          <p className="text-sm text-gray-400 mt-1">Configure all major affiliate program behaviors.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={restoreDefaults} className="text-gray-500 border-gray-200" data-testid="restore-defaults-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Restore Defaults
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="save-rules-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.map((section, si) => (
          <Card key={si} className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-50">
                <div className={`w-9 h-9 rounded-lg ${section.bg} flex items-center justify-center`}>
                  <section.icon className={`w-4.5 h-4.5 ${section.color}`} />
                </div>
                <h3 className="font-semibold text-[#0f1f33]">{section.title}</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {section.items.map((item, ii) => {
                  if (item.type === 'toggle') return <ToggleRow key={ii} {...item} />;
                  if (item.type === 'number') return <NumberInput key={ii} {...item} />;
                  if (item.type === 'info') return (
                    <div key={ii} className="flex items-center justify-between py-3">
                      <p className="text-sm font-medium text-[#0f1f33]">{item.label}</p>
                      <Badge variant="secondary" className="text-xs">{item.value}</Badge>
                    </div>
                  );
                  return null;
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
